import { NotificationKind } from 'carbon-components-react';

export type AppTheme = 'base' | 'dark';

export type OptionItem = {
  value: string;
  label: string;
};

export type AppAlert = {
  id: string;
  group: string;
  kind: NotificationKind;
  message: string;
  details: any;
  withClose: boolean;
  type?: string;
  notificationProps?: object;
  timeout?: number;
  stayForever?: boolean;
};

export type AlertOptions = {
  id?: string;
  withClose?: boolean;
  type?: string;
  notificationProps?: object;
  timeout?: number;
  stayForever?: boolean;
};

export type Options = OptionItem[];

export type domainsResponse = {
  code: string;
  label: string;
  sequence: number;
};

export enum AUTH_TYPE {
  BASIC_LOGIN = 'BASIC_LOGIN',
  SSO_LOGIN = 'SSO_LOGIN',
  OPT_LOGIN = 'OTP_LOGIN',
  SMARTPHONE_LOGIN = 'SMARTPHONE_LOGIN',
  EXTERNAL_APP_LOGIN = 'EXTERNAL_APP_LOGIN'
}

export enum USERSROLE {
  SYSADMIN = 'SYSADMIN',
  DBYDADMIN = 'DBYDADMIN',
  AOADMIN = 'AOADMIN',
  AOVIEWER = 'AOVIEWER',
  AOUSER = 'AOUSER',
  ENQUIRER = 'ENQUIRER',
  SUPPORT = 'SUPPORT',
  OTHER = 'OTHER'
}

export enum VIEW_HOME_LEFT_NAV {
  ENQUIRER = 'ENQUIRER',
  OTHER = 'OTHER'
}

export enum VIEW_MEMBERS_LEFT_NAV {
  DBYDADMIN = 'DBYDADMIN'
}

export enum AREAOFINTEREST_LEFT_NAV {
  SYSADMIN = 'SYSADMIN',
  AOADMIN = 'AOADMIN'
}

export enum VIEW_DASHBOARD_LEFT_NAV {
  DBYDADMIN = 'DBYDADMIN',
  AOADMIN = 'AOADMIN',
  AOUSER = 'AOUSER'
}

export enum VIEW_SUPPORT_TOOLS_LEFT_NAV {
  SYSADMIN = 'SYSADMIN',
  SUPPORT = 'SUPPORT'
}

export enum VIEW_MANAGE_ACCESS_LEFT_NAV {
  SYSADMIN = 'SYSADMIN',
  SUPPORT = 'SUPPORT'
}

export enum VIEW_REF_CHANNEL_LEFT_NAV {
  AOADMIN = 'AOADMIN'
}

export enum VIEW_REPORT_LEFT_NAV {
  DBYDADMIN = 'DBYDADMIN',
  AOADMIN = 'AOADMIN',
  SUPPORT = 'SUPPORT',
  SYSADMIN = 'SYSADMIN'
}

export enum VIEW_MANAGE_USERS_LEFT_NAV {
  DBYDADMIN = 'DBYDADMIN',
  AOADMIN = 'AOADMIN'
}

export enum VIEW_CONFIGURATION_LEFT_NAV {
  DBYDADMIN = 'DBYDADMIN',
  AOADMIN = 'AOADMIN',
  SUPPORT = 'SUPPORT',
  SYSADMIN = 'SYSADMIN'
}

export enum VIEW_ALL_USERS {
  USERSROLE
}

export enum VIEW_SUPPORT_SYSADMIN_USERS {
  SUPPORT = 'SUPPORT',
  SYSADMIN = 'SYSADMIN'
}

export enum THEME {
  BASE = 'base',
  DARK = 'dark'
}

export enum LANGUAGE {
  EN = 'en',
  CN = 'cn'
}

export enum LOCAL_STORAGE_KEYS {
  APP_PERSIST_ROOT = 'persist:root',
  AWS_SIGNIN_HOSTED_UI = 'amplify-signin-with-hostedUI',
  AUTH_JWT_TOKEN_ID = 'AUTH_JWT_TOKEN_ID'
}

export enum CHANNELS {
  LEGACY = 'LEGACY',
  WEBHOOK = 'WEBHOOK',
  SWX_AUTOMATE = 'SMARTERWX AUTOMATE',
  FALLBACK = 'FALLBACK'
}

export interface SetAppThemePayload {
  theme: AppTheme;
}

export interface InitAppFailurePayload {
  message: string;
  details: any;
}

export interface SetAppLangPayload {
  language: LANGUAGE;
}

export const ALL_USERROLES = [
  'DBYDADMIN',
  'SUPPORT',
  'SYSADMIN',
  'ENQUIRER',
  'AOADMIN',
  'AOVIEWER',
  'AOUSER'
];

export const END_USER_PREMIUM_USERROLES = ALL_USERROLES.filter(
  (role) => role !== USERSROLE.SYSADMIN && role !== USERSROLE.DBYDADMIN
);

export const SUPPORT_SYS_ADMIN_USERROLES = ['SUPPORT', 'SYSADMIN'];

export const SUPPORT_SYS_DBYD_AO_ADMIN_USERROLES = [
  'SUPPORT',
  'SYSADMIN',
  'DBYDADMIN',
  'AOADMIN'
];

export const SUPPORT_SYS_DBYD_AO_ADMIN_AO_VIEWER_USERROLES = [
  'SUPPORT',
  'SYSADMIN',
  'DBYDADMIN',
  'AOADMIN',
  'AOVIEWER'
];

export enum APP_URL {
  ROOT_URL = '/dashboard',
  LOGIN = '/login',
  REGISTRATION = '/register',
  AUTH = '/auth',
  ONE_TIME_PASSCODE = '/oneTimePasscode',
  TEMPORARY_CODE = '/temporary/code/auth',
  EXTERNAL_APP = '/external/auth',
  AUTH_USER_TERMS = '/auth/user-terms',
  AUTH_MULTI_ORGANISATION = '/auth/multi-organisation',
  AOI_VIEWER = '/members/aoi/viewer',
  MEMBERS = '/members',
  DASHBOARD = '/dashboard',
  RESIDENTIAL = '/residential',
  RESIDENTIAL_ENQUIRY = '/residential/enquiries',
  REPORTS = '/reports',
  REPORTS_OLD = '/reports-old',
  MANAGE_INTEGRATION = '/integrations',
  SERVICES = '/services',
  SUPPORT = '/support',
  EMERGENCY = '/emergency',
  MESSAGES = '/messages',
  MOBILISATION = '/comingsoon',

  // USERS
  USERS_ADMIN = '/users',
  // ENQUIRY
  ENQUIRIY_HISTORY = '/enquiries',
  ALL_ENQUIRIY_HISTORY = '/enquiries/all',
  ALL_ENQUIRY_HISTORY_DETAILS = '/enquiries/all/:enquiryId',
  ENQUIRY_DETAILS = '/enquiries/:id',
  CERTLOC_LOCATOR = '/certloc-locator/:enquiryId',
  LODGE_NEW_ENQUIRY = '/enquiries/new',
  CLONE_EXISTING_ENQUIRY = '/enquiries/new?clone=:id',
  MANAGE_ENQUIRIES_TEMPLATES = '/enquiries/templates',
  MANAGE_ENQUIRIES_NEW_TEMPLATE = '/enquiries/templates/new',
  MANAGE_ENQUIRIES_EDIT_TEMPLATE = '/enquiries/templates/:id',
  ENQUIRY_PRESETS = '/enquiries/presets',
  // CONFIGURE SETTINGS
  CONFIGURE = '/configure',
  MANAGE_AOI = '/configure/aoi',
  MANAGE_AOI_HISTORY = '/configure/aoi/history',
  MANAGE_REFERRAL_CHANNEL = '/configure/referral',
  MANAGE_CONTACT_DETAILS = '/configure/contacts',
  MANAGE_SAFETY_MESSAGE_AND_LOGO = '/configure/safety-message-and-logo',
  MANAGE_PUBLIC_PROFILE = '/configure/info',
  ORGANISATION_PROFILE = '/configure/organisation-profile',
  INTEGRATIONS = '/integrations',
  USERS_API_KEYS = '/integrations/apikeys',
  MANAGE_WEB_HOOKS = '/integrations/webhooks',
  WEB_HOOKS_HISTORY = '/integrations/webhooks/history',
  WEB_HOOKS_SUBSCRIPTIONS = '/integrations/webhooks/subscriptions',
  SEARCH_EMAILS = '/support/emails',
  SEARCH_SUPPORT_USERS = '/support/users',
  REFERRAL_HISTORY = '/referral/history',
  ASSET_OWNER_EMAILS = '/emails/(referrals|responses)',
  REFERRALS_EMAILS = '/emails/referrals',
  RESPONSES_EMAILS = '/emails/responses',
  GIS_INTEGRATIONS = '/integrations/gis',
  UNDELIVERED_EMAIL = '/emails/undelivered',
  MANAGE_ANNOUNCEMENTS = '/announcements',
  PREMIUM_SELF_SERVICE = '/premium/self-service',
  PREMIUM_FEATURES = '/premium/features',
  PURCHASE_TRANSACTIONS = '/premium/purchase/transactions',
  BULK_ENQUIRY_LODGEMENT = '/premium/large-area-enquiries',
  BYDA_MEMBER_LOOKUP = '/premium/byda-member-lookup',
  WEB_LAUNCHER_LANDING = '/landing/:webLaunchRequestId/:pageType?',
  RESPONSE = '/response/:token',
  DOCUMENT_PREVIEW = '/documents/preview',
  ISEEKPLANT = '/iseekplant/:pageType',
  FIREANTS = '/fireants',
  ENQUIRY_LODGED = '/enquiries/lodged'
}

export enum SCREEN_SIZE {
  SM = 'SM',
  MD = 'MD',
  LG = 'LG',
  XL = 'XL',
  MAX = 'MAX'
}

// OTHER TYPES
export interface AddressType {
  line1: string | null;
  line2: string | null;
  country: string | null;
  locality: string | null;
  postcode: string | null;
  state: string | null;
}

export enum DBYD_1100_URL {
  TERMS_OF_USE = 'https://www.byda.com.au/terms-of-use/',
  PRIVACY_POLICY = 'https://www.byda.com.au/privacy-policy/',
  EMERGENCIES = 'https://www.byda.com.au/what-to-do-in-an-emergency',
  HOME = 'https://www.byda.com.au',
  LOGIN_HELP = 'https://help.byda.com.au/article/signup-login-overview/',
  HELP = 'https://help.byda.com.au',
  EMAIL_DELIVERY_EVENTS = 'https://help.byda.com.au/article/email-delivery-events/'
}
